<script setup lang="ts">
import { computed } from 'vue'

interface Props {
	rating: number
	maxStars?: number
	size?: 'sm' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
	maxStars: 5,
	showRating: true,
	size: 'md',
})

const starColor = computed(() => {
	if (!props.rating) return 'text-gray-300'
	if (props.rating < 1.5) return 'text-red-500'
	if (props.rating < 2.5) return 'text-orange-500'
	if (props.rating < 3.5) return 'text-yellow-500'
	if (props.rating < 4.5) return 'text-green-400'
	return 'text-green-500'
})

const starSize = computed(() => {
	switch (props.size) {
		case 'sm':
			return 'h-5 w-5'
		case 'lg':
			return 'h-8 w-8'
		default:
			return 'h-6 w-6'
	}
})
</script>

<template>
	<div
	    v-if="props.rating > 0"
		class="flex flex-row items-center"
		role="img"
		:aria-label="`Rating: ${rating.toFixed(1)} out of ${maxStars} stars`">
		<Icon
			v-for="(i, index) in maxStars"
			:key="i"
			:name="
				i <= Math.floor(rating)
					? 'ph:star-fill'
					: rating <= index + 1 && rating >= index + 0.5
					? 'ph-star-half-fill'
					: 'ph:star'
			"
			:class="[
				starSize,
				'flex-none',
				i <= Math.ceil(rating) ? starColor : 'text-gray-200 hidden',
				rating <= 0 ? 'text-gray-300 block' : '',
			]"
			aria-hidden="true" />
	</div>
	<div
		v-else
	>
	<Icon
			v-for="(i) in maxStars"
			:key="i"
			name="ph:star"
			:class="[
				starSize,
				'flex-none text-gray-300',
			]"
			aria-hidden="true" />
	</div>
</template>
